import useSymbol from '@/hooks/use-symbol';
import { useAccountStore } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { cn } from '@/utils/cn';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import { formatNumber, parseDecimalToBigInt } from '@/utils/value-format';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { getEstFillPrice, getSlippage } from '../../utils/math';
import { MaxSlippageInput } from '../max-slippage-input';
import { OrderFormSchema } from '../order-form';
import { FormItem } from '../order-form-components';

export default function Slippage({
  isSubmitSuccessful,
}: {
  isSubmitSuccessful?: boolean;
}) {
  const symbol = useSymbol();
  const { t } = useTranslation();

  const sizeDecimals = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.sizeDecimals,
  );

  const form = useFormContext<OrderFormSchema>();
  const isBuy = form.watch('order.isBuy');
  const size = parseDecimalToBigInt(form.watch('order.size'), sizeDecimals);
  const isEval = useAccountStore((state) =>
    getIsStatusEval(state.getCapitalStatus(state.address)),
  );

  const slippage = useStore(useMarketStore(symbol), (state) => {
    const estFillPrice = getEstFillPrice(
      isBuy,
      size,
      state.book,
      state.marketSpec.priceDecimals,
      isEval,
    );
    if (!estFillPrice) return;
    if (estFillPrice.bigint === null) return;
    return getSlippage(
      symbol,
      isBuy,
      estFillPrice.bigint,
      isEval
        ? undefined
        : {
            size,
            visibleCost: estFillPrice.visibleCost,
            visibleFilled: estFillPrice.visibleFilled,
            book: state.book,
          },
    )?.decimal;
  });

  return (
    <FormItem className="flex h-10 items-center justify-between">
      <p className="flex items-center gap-2">{t('orderForm.slippage')}</p>
      <div className="flex items-center gap-3">
        <div className="flex items-end gap-2">
          <span
            className={cn(
              'font-mono leading-tight text-foreground',
              slippage && Number(Number(slippage).toFixed(2)) <= 0 // TEMP: ugly
                ? 'text-green'
                : null,
            )}
          >
            {formatNumber(slippage, {
              digits: 4,
              style: 'slippage',
            })}
          </span>
          <span className="px-1 leading-none">/</span>
          <span className="text-sm leading-tight text-vestgrey-600">
            {t('orderForm.max')}
          </span>
        </div>
        <div className="group flex items-center bg-vestgrey-800 transition-colors hover:bg-border">
          <MaxSlippageInput
            symbol={symbol}
            isBuy={isBuy}
            isSubmitSuccessful={isSubmitSuccessful}
          />
        </div>
      </div>
    </FormItem>
  );
}
